import classnames from 'classnames';
import { Panel, Separator } from '@sorrem-client/ui/components';
import type { BodyPart, EquipmentPiece } from '@sorrem/shared';
import type { CharacterCreationDraft } from '../../hooks';
import { EYEBROWS_SLOT, FACIAL_HAIR_SLOT, HAIR_SLOT } from '../../hooks';
import { BodyPartPicker } from '@sorrem-client/ui/features/Character/components/BodyPartPicker';
import { HairColorPicker } from '@sorrem-client/ui/features/Character/components/HairColorPicker';
import { StartingEquipmentPicker } from '@sorrem-client/ui/features/Character/components/StartingEquipmentPicker';
import { useAppearanceThumbnails } from '@sorrem-client/ui/features/Character/use-appearance-thumbnails';
import { useGetStartingEquipmentQuery } from '@sorrem-client/ui/store';
import styles from './CreationAppearancePanel.module.scss';

const THUMBNAIL_SLOTS = [HAIR_SLOT, EYEBROWS_SLOT, FACIAL_HAIR_SLOT] as const;
/** The weapon stats worth a glance while creating; the hover preview shows the rest. */
const WEAPON_SUMMARY_STAT_KEYS = ['damage', 'melee'] as const;

interface CreationAppearancePanelProps {
    draft: CharacterCreationDraft;
    bodyParts: readonly BodyPart[];
    equipmentPieces: readonly EquipmentPiece[];
}

/** Character creation's side-panel content: hairstyle/eyebrows/facial-hair/hair-color, then the starting outfit (armor) and weapon. */
export function CreationAppearancePanel({ draft, bodyParts, equipmentPieces }: CreationAppearancePanelProps): JSX.Element {
    const thumbnails = useAppearanceThumbnails(bodyParts, draft.selectedBaseModel, THUMBNAIL_SLOTS, draft.outfitModifiers);
    const { data: startingEquipment } = useGetStartingEquipmentQuery();

    return (
        <Panel className={styles.appearancePanel} glow>
            {/* Eyebrows have only a couple of styles, so they sit beside the hairstyles. */}
            <div className={styles.pickerRow}>
                <div className={classnames(styles.pickerColumn, styles.fill)}>
                    <h3 className={styles.panelTitle}>Hairstyle</h3>
                    <Separator />
                    <BodyPartPicker
                        slot={HAIR_SLOT}
                        bodyParts={bodyParts}
                        characterModelId={draft.selectedBaseModel}
                        selectedId={draft.hairstyleId}
                        onSelect={draft.setHairstyleId}
                        thumbnails={thumbnails}
                        isDisabled={draft.isSaving}
                    />
                </div>

                <Separator orientation="vertical" className={styles.verticalSeparator} />

                <div className={styles.pickerColumn}>
                    <h3 className={styles.panelTitle}>Eyebrows</h3>
                    <Separator />
                    <BodyPartPicker
                        slot={EYEBROWS_SLOT}
                        bodyParts={bodyParts}
                        characterModelId={draft.selectedBaseModel}
                        selectedId={draft.eyebrowsId}
                        onSelect={draft.setEyebrowsId}
                        thumbnails={thumbnails}
                        isDisabled={draft.isSaving}
                    />
                </div>
            </div>

            {draft.hasFacialHairOptions && (
                <>
                    <h3 className={styles.panelTitle}>Facial Hair</h3>
                    <Separator />
                    <BodyPartPicker
                        slot={FACIAL_HAIR_SLOT}
                        bodyParts={bodyParts}
                        characterModelId={draft.selectedBaseModel}
                        selectedId={draft.facialHairId}
                        onSelect={draft.setFacialHairId}
                        thumbnails={thumbnails}
                        allowNone
                        isDisabled={draft.isSaving}
                    />
                </>
            )}

            <h3 className={styles.panelTitle}>Hair Color</h3>
            <Separator />
            <HairColorPicker
                swatches={draft.hairColorSwatches}
                selectedId={draft.hairColorId}
                onSelect={draft.setHairColorId}
                isDisabled={draft.isSaving}
            />

            {/* The outfit is the starting armor; it and the weapon each have only a few choices, so they share a row. */}
            <div className={styles.pickerRow}>
                <div className={styles.pickerColumn}>
                    <h3 className={styles.panelTitle}>Outfit</h3>
                    <Separator />
                    <StartingEquipmentPicker
                        options={draft.armorOptions}
                        catalog={startingEquipment?.armor ?? []}
                        equipmentPieces={equipmentPieces}
                        characterModelId={draft.selectedBaseModel}
                        selectedId={draft.armorItemId}
                        onSelect={draft.setArmorItemId}
                        isDisabled={draft.isSaving}
                    />
                </div>

                <Separator orientation="vertical" className={styles.verticalSeparator} />

                <div className={classnames(styles.pickerColumn, styles.fill)}>
                    <h3 className={styles.panelTitle}>Weapon</h3>
                    <Separator />
                    <StartingEquipmentPicker
                        options={draft.weaponOptions}
                        catalog={startingEquipment?.weapons ?? []}
                        summaryStatKeys={WEAPON_SUMMARY_STAT_KEYS}
                        equipmentPieces={equipmentPieces}
                        characterModelId={draft.selectedBaseModel}
                        selectedId={draft.weaponItemId}
                        onSelect={draft.setWeaponItemId}
                        isDisabled={draft.isSaving}
                    />
                </div>
            </div>
        </Panel>
    );
}
